import { createContext, useContext, useReducer, ReactNode } from 'react';

export type Attachment = {
  type: 'file_reference';
  path: string;
  startLine: number;
  endLine: number;
  side: 'LEFT' | 'RIGHT';
  code: string;
};

// State interface
interface SidebarState {
  isRightSidebarOpen: boolean;
  activeCommentGid: string | null;
  activeFilePath: string | null;
  activeLineNumber: number | null;
  activeSide: 'LEFT' | 'RIGHT' | null;
  activeTab: string;
  aiChatInput: string;
  aiChatAttachments: Attachment[];
  areResolvedCommentsCollapsed: boolean;
}

// Action types
type SidebarAction =
  | { type: 'SET_RIGHT_SIDEBAR_OPEN'; payload: boolean }
  | { type: 'SET_ACTIVE_COMMENT_GID'; payload: string | null }
  | { type: 'SET_ACTIVE_FILE_PATH'; payload: string | null }
  | { type: 'SET_ACTIVE_LINE_NUMBER'; payload: number | null }
  | { type: 'SET_ACTIVE_SIDE'; payload: 'LEFT' | 'RIGHT' | null }
  | { type: 'SET_ACTIVE_TAB'; payload: string }
  | { type: 'SET_AI_CHAT_INPUT'; payload: string }
  | { type: 'SET_AI_CHAT_ATTACHMENTS'; payload: Attachment[] }
  | { type: 'SET_RESOLVED_COMMENTS_COLLAPSED'; payload: boolean }
  | { type: 'NAVIGATE_TO_COMMENT'; payload: string }
  | { type: 'NAVIGATE_TO_LOCATION'; payload: { path: string; line: number; side: 'LEFT' | 'RIGHT' } }
  | { type: 'NAVIGATE_TO_AI_CHAT'; payload?: { inputMessage?: string; attachments?: Attachment[] } }
  | { type: 'RESET_COMMENT_FILTERS' };

// Initial state
const initialState: SidebarState = {
  isRightSidebarOpen: false,
  activeCommentGid: null,
  activeFilePath: null,
  activeLineNumber: null,
  activeSide: null,
  activeTab: 'comments',
  aiChatInput: '',
  aiChatAttachments: [],
  areResolvedCommentsCollapsed: true,
};

// Reducer function
function sidebarReducer(state: SidebarState, action: SidebarAction): SidebarState {
  switch (action.type) {
    case 'SET_RIGHT_SIDEBAR_OPEN':
      return { ...state, isRightSidebarOpen: action.payload };

    case 'SET_ACTIVE_COMMENT_GID':
      return { ...state, activeCommentGid: action.payload };

    case 'SET_ACTIVE_FILE_PATH':
      return { ...state, activeFilePath: action.payload };

    case 'SET_ACTIVE_LINE_NUMBER':
      return { ...state, activeLineNumber: action.payload };

    case 'SET_ACTIVE_SIDE':
      return { ...state, activeSide: action.payload };

    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };

    case 'SET_AI_CHAT_INPUT':
      return { ...state, aiChatInput: action.payload };

    case 'SET_AI_CHAT_ATTACHMENTS':
      return { ...state, aiChatAttachments: action.payload };

    case 'SET_RESOLVED_COMMENTS_COLLAPSED':
      return { ...state, areResolvedCommentsCollapsed: action.payload };

    case 'NAVIGATE_TO_COMMENT':
      return {
        ...state,
        isRightSidebarOpen: true,
        activeCommentGid: action.payload,
        activeTab: 'comments',
        // Reset other filters
        activeFilePath: null,
        activeLineNumber: null,
        activeSide: null,
      };

    case 'NAVIGATE_TO_LOCATION':
      return {
        ...state,
        isRightSidebarOpen: true,
        activeFilePath: action.payload.path,
        activeLineNumber: action.payload.line,
        activeSide: action.payload.side,
        activeTab: 'comments',
        // Reset comment GID
        activeCommentGid: null,
      };

    case 'NAVIGATE_TO_AI_CHAT':
      return {
        ...state,
        isRightSidebarOpen: true,
        activeTab: 'ai-chat',
        // Reset comment filters
        activeCommentGid: null,
        activeFilePath: null,
        activeLineNumber: null,
        activeSide: null,
        // Set AI chat input and context if provided
        aiChatInput: action.payload?.inputMessage || state.aiChatInput,
        aiChatAttachments: action.payload?.attachments || state.aiChatAttachments,
      };

    case 'RESET_COMMENT_FILTERS':
      return {
        ...state,
        activeCommentGid: null,
        activeFilePath: null,
        activeLineNumber: null,
        activeSide: null,
      };

    default:
      return state;
  }
}

// Context type with dispatch and state - legacy setters removed
type SidebarContextType = SidebarState & {
  dispatch: React.Dispatch<SidebarAction>;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sidebarReducer, initialState);

  return (
    <SidebarContext.Provider
      value={{
        // State values
        ...state,
        // Dispatch for reducer actions
        dispatch,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

// Helper methods for common operations using reducer actions
export function useCommentNavigation() {
  const { dispatch } = useSidebar();

  const navigateToComment = (commentGid: string) => {
    dispatch({ type: 'NAVIGATE_TO_COMMENT', payload: commentGid });
  };

  const navigateToLocation = (path: string, line: number, side: 'LEFT' | 'RIGHT') => {
    dispatch({ type: 'NAVIGATE_TO_LOCATION', payload: { path, line, side } });
  };

  const navigateToAIChat = (inputMessage?: string, attachments?: Attachment[]) => {
    dispatch({ type: 'NAVIGATE_TO_AI_CHAT', payload: { inputMessage, attachments } });
  };

  const resetCommentFilters = () => {
    dispatch({ type: 'RESET_COMMENT_FILTERS' });
  };

  return {
    navigateToComment,
    navigateToLocation,
    navigateToAIChat,
    resetCommentFilters,
  };
}

// New action-based hooks for cleaner patterns
export function useSidebarActions() {
  const { dispatch } = useSidebar();

  return {
    setRightSidebarOpen: (open: boolean) => dispatch({ type: 'SET_RIGHT_SIDEBAR_OPEN', payload: open }),

    navigateToComment: (commentGid: string) => dispatch({ type: 'NAVIGATE_TO_COMMENT', payload: commentGid }),
    navigateToLocation: (path: string, line: number, side: 'LEFT' | 'RIGHT') =>
      dispatch({ type: 'NAVIGATE_TO_LOCATION', payload: { path, line, side } }),
    navigateToAIChat: (inputMessage?: string, attachments?: Attachment[]) =>
      dispatch({ type: 'NAVIGATE_TO_AI_CHAT', payload: { inputMessage, attachments } }),
    resetCommentFilters: () => dispatch({ type: 'RESET_COMMENT_FILTERS' }),
  };
}
